import { useScrollReveal } from '@/hooks/useScrollReveal';

export default function BreakfastSection() {
  const { ref: contentRef, isVisible: contentVisible } = useScrollReveal({ threshold: 0.15 });

  return (
    <section id="kahvalti" className="w-full py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-background-50">
      <div className="max-w-7xl mx-auto">
        <div ref={contentRef} className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
          {/* Image */}
          <div className={`relative rounded-3xl overflow-hidden h-72 md:h-96 lg:h-[500px] transition-all duration-700 ${contentVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <img
              src="https://readdy.ai/api/search-image?query=Traditional%20Turkish%20village%20breakfast%20spread%20on%20wooden%20table%20in%20garden%20setting%2C%20fresh%20organic%20cheese%20olives%20tomatoes%20cucumbers%20honey%20bread%20tea%20glasses%2C%20green%20nature%20background%2C%20warm%20morning%20sunlight%2C%20rustic%20aesthetic%2C%20editorial%20food%20photography%2C%20high%20detail%20colorful%20fresh%20local%20produce%2C%20serene%20peaceful%20outdoor%20dining%20atmosphere&width=800&height=600&seq=breakfast-01&orientation=landscape"
              alt="Serpme Köy Kahvaltısı"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
          </div>

          {/* Content */}
          <div className={`transition-all duration-700 delay-200 ${contentVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <h2 className="text-2xl md:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
              Güne{' '}
              <span className="text-primary-500">Serpme Köy Kahvaltısı</span>yla
              <br />
              Başlayın
            </h2>
            <p className="text-sm md:text-base text-foreground-600 mb-6 md:mb-8 leading-relaxed">
              Otelimizin restoranında gün boyunca özenle hazırlanan lezzetleri tadabilir, güne ise yöresel ürünlerle hazırlanan zengin serpme köy kahvaltımızla keyifli bir başlangıç yapabilirsiniz.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6 md:mb-8">
              {[
                { icon: 'ri-leaf-line', text: 'Yöresel ürünler' },
                { icon: 'ri-restaurant-2-line', text: 'Zengin serpme köy kahvaltısı' },
                { icon: 'ri-store-2-line', text: 'Otel restoranı' },
                { icon: 'ri-tree-line', text: 'Doğa içinde keyifli lezzet deneyimi' },
              ].map((item, i) => (
                <div
                  key={item.text}
                  className={`flex items-center gap-3 p-3 bg-secondary-50 rounded-xl transition-all duration-500 ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                  style={{ transitionDelay: `${300 + i * 100}ms` }}
                >
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 shrink-0">
                    <i className={`${item.icon} text-base`} />
                  </div>
                  <span className="text-sm font-medium text-foreground-800">{item.text}</span>
                </div>
              ))}
            </div>

            <a
              href="https://wa.me/905395851199?text=Merhaba%2C%20İlkim%20Butik%20Otel%20için%20kahvaltılı%20tatil%20fiyat%20ve%20müsaitlik%20bilgisi%20almak%20istiyorum.%20Fiyat%20teklifi%20alabilir%20miyim%3F"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-3 bg-primary-500 text-background-50 rounded-xl font-semibold text-sm hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer hover:scale-[1.02] active:scale-[0.98]"
            >
              <i className="ri-whatsapp-line" />
              Kahvaltılı Tatil İçin Fiyat Sor
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

export function PoolSection() {
  const { ref: contentRef, isVisible: contentVisible } = useScrollReveal({ threshold: 0.15 });

  return (
    <section id="havuz" className="w-full py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-secondary-50">
      <div className="max-w-7xl mx-auto">
        <div ref={contentRef} className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
          {/* Content - left on desktop */}
          <div className={`order-2 lg:order-1 transition-all duration-700 ${contentVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <h2 className="text-2xl md:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
              Açık Havuz ve{' '}
              <span className="text-accent-500">Huzurlu</span>
              <br />
              Dinlenme Alanları
            </h2>
            <p className="text-sm md:text-base text-foreground-600 mb-6 md:mb-8 leading-relaxed">
              Tatiliniz boyunca açık yüzme havuzumuzda serinleyebilir, dinlenme alanlarımızda Orhaniye'nin sakin atmosferinin tadını çıkarabilirsiniz.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6 md:mb-8">
              {[
                { icon: 'ri-drop-line', text: 'Açık yüzme havuzu' },
                { icon: 'ri-armchair-line', text: 'Sakin dinlenme alanları' },
                { icon: 'ri-tree-line', text: 'Doğa içinde huzurlu atmosfer' },
                { icon: 'ri-heart-3-line', text: 'Aile sıcaklığında butik otel deneyimi' },
              ].map((item, i) => (
                <div
                  key={item.text}
                  className={`flex items-center gap-3 p-3 bg-background-50 rounded-xl border border-background-200/60 transition-all duration-500 ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                  style={{ transitionDelay: `${300 + i * 100}ms` }}
                >
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-50 text-accent-500 shrink-0">
                    <i className={`${item.icon} text-base`} />
                  </div>
                  <span className="text-sm font-medium text-foreground-800">{item.text}</span>
                </div>
              ))}
            </div>

            <a
              href="#fiyat-form"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector('#fiyat-form')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="inline-flex items-center gap-2 px-5 py-3 bg-primary-500 text-background-50 rounded-xl font-semibold text-sm hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer hover:scale-[1.02] active:scale-[0.98]"
            >
              <i className="ri-calendar-check-line" />
              Havuzlu Tatil İçin Tarih Seç
            </a>
          </div>

          {/* Image - right on desktop */}
          <div className={`order-1 lg:order-2 relative rounded-3xl overflow-hidden h-72 md:h-96 lg:h-[500px] transition-all duration-700 delay-200 ${contentVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <img
              src="https://readdy.ai/api/search-image?query=Serene%20boutique%20hotel%20outdoor%20swimming%20pool%20surrounded%20by%20lush%20greenery%2C%20olive%20trees%20and%20palm%20trees%2C%20sun%20loungers%20with%20white%20cushions%2C%20turquoise%20water%20reflecting%20sky%2C%20warm%20summer%20afternoon%20light%2C%20peaceful%20relaxation%20atmosphere%2C%20Mediterranean%20garden%20setting%2C%20editorial%20travel%20photography%20high%20detail&width=800&height=600&seq=pool-01&orientation=landscape"
              alt="Açık Yüzme Havuzu"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
          </div>
        </div>
      </div>
    </section>
  );
}